// DEPLOY: /var/www/woody.wtf/frontend/src/chart/AddToList.js
// "+ List" dialog: add the chart's ticker to an existing list or a new one (/api/lists).
// Same behaviour as the old chart's dialog.
import React, { useState, useEffect } from 'react';
import api from '../api';

const S = {
  modalOverlay: { position: 'fixed', inset: 0, background: 'var(--overlay)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal:        { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'calc(var(--radius)*2)', padding: '28px', width: '420px', maxWidth: '95vw', boxShadow: '0 8px 32px rgba(0,0,0,0.2)', boxSizing: 'border-box' },
  modalTitle:   { fontSize: '16px', fontWeight: 700, marginBottom: '16px', color: 'var(--text)' },
  modalInput:   { width: '100%', padding: '7px 10px', fontSize: '13px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg)', color: 'var(--text)', fontFamily: "'Space Grotesk',sans-serif", boxSizing: 'border-box', marginTop: '4px' },
  modalLabel:   { fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', fontFamily: "'JetBrains Mono',monospace", display: 'block', marginBottom: '4px' },
  modalSelect:  { width: '100%', padding: '7px 10px', fontSize: '13px', border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--bg)', color: 'var(--text)', fontFamily: "'JetBrains Mono',monospace", boxSizing: 'border-box' },
  modalFooter:  { display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px' },
  modalError:   { color: 'var(--error)', fontSize: '12px', marginTop: '6px' },
  modalSuccess: { color: 'var(--success)', fontSize: '12px', marginTop: '6px' },
  btnSm:        { background: 'var(--accent)', color: 'var(--on-accent)', border: 'none', borderRadius: 'var(--radius)', padding: '4px 12px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' },
  btnGhost:     { background: 'none', color: 'var(--text-muted)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '4px 10px', fontSize: '12px', cursor: 'pointer' },
};

export default function AddToListModal({ ticker, onClose }) {
  const [lists,     setLists]     = useState([]);
  const [mode,      setMode]      = useState('existing');
  const [selListId, setSelListId] = useState('');
  const [newName,   setNewName]   = useState('');
  const [busy,      setBusy]      = useState(false);
  const [msg,       setMsg]       = useState('');
  const [err,       setErr]       = useState('');

  useEffect(() => {
    api.get('/lists').then(r => {
      const l = r.data.lists || [];
      setLists(l);
      if (l.length > 0) setSelListId(String(l[0].id));
      else setMode('new');
    }).catch(() => setMode('new'));
  }, []);

  const submit = async () => {
    setBusy(true); setErr(''); setMsg('');
    try {
      let listId;
      if (mode === 'new') {
        if (!newName.trim()) { setErr('List name required'); setBusy(false); return; }
        const r = await api.post('/lists', { name: newName.trim() });
        listId = r.data.list.id;
      } else {
        listId = parseInt(selListId);
        if (!listId) { setErr('Select a list'); setBusy(false); return; }
      }
      await api.post(`/lists/${listId}/items`, { ticker }).catch(e => {
        if (e.response?.status !== 409) throw e;
      });
      setMsg('Added to list');
      setTimeout(() => onClose(), 1500);
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed to add ticker');
    } finally { setBusy(false); }
  };

  return (
    <div style={S.modalOverlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={S.modal}>
        <div style={S.modalTitle}>Add {ticker} to list</div>

        {lists.length > 0 && (
          <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
            <button
              style={{ ...S.btnSm, background: mode === 'existing' ? 'var(--accent)' : 'var(--bg)', color: mode === 'existing' ? 'var(--on-accent)' : 'var(--text-muted)', border: '1px solid var(--border)' }}
              onClick={() => setMode('existing')}
            >Existing List</button>
            <button
              style={{ ...S.btnSm, background: mode === 'new' ? 'var(--accent)' : 'var(--bg)', color: mode === 'new' ? 'var(--on-accent)' : 'var(--text-muted)', border: '1px solid var(--border)' }}
              onClick={() => setMode('new')}
            >New List</button>
          </div>
        )}

        {mode === 'existing' && lists.length > 0 ? (
          <div>
            <label style={S.modalLabel}>Select List</label>
            <select style={S.modalSelect} value={selListId} onChange={e => setSelListId(e.target.value)}>
              {lists.map(l => <option key={l.id} value={l.id}>{l.name} ({l.item_count} tickers)</option>)}
            </select>
          </div>
        ) : (
          <div>
            <label style={S.modalLabel}>New List Name</label>
            <input style={S.modalInput} value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g. ASX Watchlist" maxLength={100} />
          </div>
        )}

        {err && <div style={S.modalError}>{err}</div>}
        {msg && <div style={S.modalSuccess}>{msg}</div>}

        <div style={S.modalFooter}>
          <button style={S.btnGhost} onClick={onClose} disabled={busy}>Cancel</button>
          <button style={S.btnSm} onClick={submit} disabled={busy}>{busy ? 'Adding…' : 'Add to List'}</button>
        </div>
      </div>
    </div>
  );
}
